import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { CalendarCheck, Smartphone, Check, X, Rocket, ChevronRight } from 'lucide-react';

export const OnboardingChecklist = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [cal, setCal] = useState(null);
  const [msg, setMsg] = useState(null);
  const [ready, setReady] = useState(false);
  const dismissKey = user ? `tempo_onboarding_dismissed_${user.user_id}` : 'tempo_onboarding_dismissed';
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(dismissKey) === '1'; } catch (e) { return false; }
  });

  const load = useCallback(async () => {
    try {
      const [c, m] = await Promise.all([api.get('/calendar/status'), api.get('/messaging/status')]);
      setCal(c.data); setMsg(m.data);
    } catch (e) { /* ignore */ } finally { setReady(true); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!ready || dismissed) return null;

  const calDone = !!(cal && (cal.connections?.google?.connected || cal.connections?.outlook?.connected));
  const msgDone = !!(msg && (msg.configured?.sms || msg.configured?.whatsapp || msg.configured?.telegram));

  const steps = [
    { id: 'calendar', icon: CalendarCheck, title: 'Connect your calendar', desc: 'Two-way sync with Google or Outlook so your day plans around real events.', done: calDone },
    { id: 'messaging', icon: Smartphone, title: 'Add your messaging keys', desc: 'Bring your own Twilio or Telegram keys to text your coach and get gentle nudges.', done: msgDone },
  ];
  const doneCount = steps.filter((s) => s.done).length;

  // Once everything is connected, quietly retire the checklist.
  if (doneCount === steps.length) return null;

  const dismiss = () => {
    try { localStorage.setItem(dismissKey, '1'); } catch (e) {}
    setDismissed(true);
  };

  return (
    <div className="rounded-2xl border p-5 bg-card relative overflow-hidden" style={{ borderColor: 'hsl(var(--primary) / 0.4)' }} data-testid="onboarding-checklist">
      <button onClick={dismiss} aria-label="Skip setup" className="absolute top-3 right-3 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors" data-testid="onboarding-dismiss">
        <X className="h-4 w-4" />
      </button>
      <div className="flex items-center gap-2">
        <div className="h-9 w-9 rounded-xl grid place-items-center shrink-0" style={{ background: 'hsl(var(--accent))' }}><Rocket className="h-4.5 w-4.5" style={{ color: 'hsl(var(--primary))' }} /></div>
        <div>
          <h3 className="font-display font-700">Welcome to Larkwell — let's get you set up</h3>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Two quick steps to make your day run itself.</p>
        </div>
      </div>

      <div className="mt-3 flex items-center gap-3">
        <Progress value={(doneCount / steps.length) * 100} className="h-2 flex-1" data-testid="onboarding-progress" />
        <span className="text-xs font-600 text-[hsl(var(--muted-foreground))] shrink-0">{doneCount} of {steps.length} done</span>
      </div>

      <div className="mt-4 space-y-2">
        {steps.map((s) => (
          <div key={s.id} className="flex items-center gap-3 rounded-xl border border-[hsl(var(--border))] p-3" data-testid={`onboarding-step-${s.id}`}>
            <div className={`h-8 w-8 rounded-full grid place-items-center shrink-0 ${s.done ? '' : 'border border-[hsl(var(--border))]'}`} style={s.done ? { background: 'hsl(var(--success))' } : { background: 'hsl(var(--secondary))' }}>
              {s.done ? <Check className="h-4 w-4 text-white" /> : <s.icon className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-600 ${s.done ? 'line-through text-[hsl(var(--muted-foreground))]' : ''}`}>{s.title}</p>
              <p className="text-[11px] text-[hsl(var(--muted-foreground))]">{s.desc}</p>
            </div>
            {s.done ? (
              <span className="text-[11px] font-600" style={{ color: 'hsl(var(--success))' }} data-testid={`onboarding-done-${s.id}`}>Connected</span>
            ) : (
              <Button size="sm" onClick={() => navigate('/app/settings')} className="rounded-xl gap-1 shrink-0" style={{ background: 'hsl(var(--primary))' }} data-testid={`onboarding-cta-${s.id}`}>Set up <ChevronRight className="h-3.5 w-3.5" /></Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default OnboardingChecklist;
